import {
  type ProductResponse,
  type PdpProductAndCoupon,
} from '@/features/shop/services/Product/types'
import { normalizeProduct } from '@/features/shared/utils/normalizeProduct'
import { apiGet, apiPost } from '@/features/shared/utils/dataFetching'
import { type QueryFunctionContext } from '@tanstack/react-query'
import {
  type ProductChildrenQueryKey,
  type ProductUniversalIdQueryKey,
  type PdpProductQueryKey,
  type ProductsQueryKey,
} from '@/features/shop/services/Product/queries'
import { fetchGraphql } from '@/features/shared/utils/dataFetching/reactQuery/graphql'
import {
  ProductUniversalIdDocument,
  PdpProductDocument,
  ProductFieldsFragmentDoc,
} from '@/gql/graphql'
import { mapFromGqlProductToProduct } from '@/features/shop/services/Product/utils/gql-mapping'
import { getFragmentData } from '@/gql'

export const fetchProducts = async (context: QueryFunctionContext<ProductsQueryKey>) => {
  const { storeParams, product_ids } = context.queryKey[1]

  const data = await apiPost<ProductResponse>({
    config: {
      url: '/product-broker/v1/product',
      data: {
        ...storeParams,
        product_ids,
      },
    },
    context,
    fetcherName: 'fetchProducts',
  })
  return data.hits.map((product) => normalizeProduct(product))
}

export const fetchProductsChildren = async (
  context: QueryFunctionContext<ProductChildrenQueryKey>,
) => {
  const { parentId, storeParams } = context.queryKey[1]

  const data = await apiGet<ProductResponse>({
    config: {
      url: `/product-broker/v1/product/${parentId}/children`,
      params: storeParams,
    },
    context,
    fetcherName: 'fetchProductsChildren',
  })
  return data.hits.map((product) => normalizeProduct(product))
}

type ProductCoupon = PdpProductAndCoupon['productCoupon']
export const fetchPdpProduct = async (
  context: QueryFunctionContext<PdpProductQueryKey>,
): Promise<PdpProductAndCoupon> => {
  const { productId, storeParams } = context.queryKey[1]

  const gqlProductResponse = await fetchGraphql(
    PdpProductDocument,
    {
      productId: productId.toString(),
      retailerId: storeParams.store_id?.toString() ?? '0',
      storeLocationId: storeParams.store_location_id?.toString() ?? '0',
      metroId: storeParams.metro_id?.toString() ?? '0',
    },
    {
      context,
      // don't throw on gql error
      throwOnGqlError: false,
      // we want this call to be made for all users, logged in an non logged in. This flag allows for this
      authOptional: true,
    },
  )
    // catch gql error so that gql call for control group doesn't take down PDP.
    .catch(() => ({ product: undefined }))

  const gqlProduct = gqlProductResponse.product
    ? mapFromGqlProductToProduct(gqlProductResponse.product)
    : undefined

  // gql productCoupon
  let gqlProductCoupon: ProductCoupon

  const productFragment = getFragmentData(ProductFieldsFragmentDoc, gqlProductResponse.product)

  if (productFragment?.coupons?.[0]) {
    const firstCoupon = productFragment.coupons[0]
    gqlProductCoupon = {
      couponId: firstCoupon.id,
      description: firstCoupon.description,
      disclaimer: firstCoupon.disclaimer,
      summary: firstCoupon.summary ?? undefined,
      faceValue: firstCoupon.faceValue / 100, // value comes in cents, need to convert to dollars
      expiredAt: String(firstCoupon.expiresAt ?? ''),
      clippedAt: String(firstCoupon.userState?.clippedAt ?? ''),
      appliedProductQty: firstCoupon.requiredQty ?? 0,
    }
  }

  return {
    product: gqlProduct,
    productCoupon: gqlProductCoupon,
  }
}

// Resolves a product's universalId (and displayName) from its productId alone.
// Uses settingOptional/showInactive so the product resolves even when it is not
// available at the request's store context, which is required to redirect crawlers
// from legacy /shop/products/[productId] urls to the full/canonical PDP url.
export const fetchProductUniversalId = async (
  context: QueryFunctionContext<ProductUniversalIdQueryKey>,
) => {
  const { productId, storeParams } = context.queryKey[1]

  const response = await fetchGraphql(
    ProductUniversalIdDocument,
    {
      productId: productId.toString(),
      retailerId: storeParams.store_id?.toString() ?? '0',
      storeLocationId: storeParams.store_location_id?.toString() ?? '0',
      metroId: storeParams.metro_id?.toString() ?? '0',
    },
    {
      context,
      // Keep any response that still returned a product so the caller can determine whether to redirect or render the legacy PDP page. Only throw if there is no product.
      onError: (result) => Boolean(result.data?.product),
      // Known-benign product-broker error for inactive products; log as info, not error.
      ignoredMessages: [/type found on product is undefined in the enum/],
      authOptional: true,
    },
  )

  return response.product
}
